import Konva from 'konva';
import type { PinDef } from '../../core/library/component-def.js';
import type { PinState } from '../../core/model/pin.js';
import { computePinPath3D } from '../../core/geometry/pin-path.js';

/**
 * Renderer 2D de un pin.
 *
 * Estrategia simple:
 *  - Konva.Line con la polilínea de puntos (proyectados a XY).
 *  - Trazo base: diámetro del pin, color metálico, lineJoin y lineCap
 *    "round" → dobleces redondeados automáticamente.
 *  - Trazo highlight: 35 % del diámetro, color más claro, encima del
 *    base para simular reflejo cilíndrico.
 *  - El trazo base recibe atributos { nodeKind: 'pin', pinId } para el
 *    sistema de eventos.
 */

export interface PinRenderOptions {
  metalColor: string;
  highlightColor?: string;
  /** Solo aplica a shape="flat": desplaza el arranque del render 0.3mm
   *  hacia el cuerpo para simular que el pin sale del interior (usado en
   *  DIP invertido, mirando al revés). */
  flipped?: boolean;
  /** Rotación del componente en grados (para orientar etiquetas). */
  rotationDeg?: number;
  /** Etiqueta a dibujar sobre la parte ancha del pin (número de pin en DIPs).
   *  Si es undefined o "", no se dibuja. */
  pinNumber?: string;
}

export function renderPin(def: PinDef, state: PinState, opts: PinRenderOptions): Konva.Group {
  const group = new Konva.Group();
  group.setAttr('pinId', def.id);

  const path = computePinPath3D(def, state);
  if (path.length < 2) return group;

  const metal = opts.metalColor;
  const highlight = opts.highlightColor ?? '#f4f4f4';

  if ((def.shape ?? 'round') === 'flat') {
    return renderFlatPin(group, def, path, metal, highlight, opts.flipped ?? false, opts.rotationDeg ?? 0, opts.pinNumber ?? '');
  }

  // Aplana la polilínea con fillet aproximado en cada vértice interior:
  // cada doblez visible se sustituye por dos puntos de tangencia y un
  // arco discreto de radio = diámetro del pin (⇒ diámetro visible 2×).
  const pts = buildSmoothPolyline(path.map((v) => v.position), def.diameter_mm);

  const base = new Konva.Line({
    points: pts,
    stroke: metal,
    strokeWidth: def.diameter_mm,
    lineCap: 'round',
    lineJoin: 'round',
    listening: true,
    shadowForStrokeEnabled: false,
    hitStrokeWidth: def.diameter_mm * 1.4,
  });
  base.setAttr('nodeKind', 'pin');
  base.setAttr('pinId', def.id);
  group.add(base);

  const hi = new Konva.Line({
    points: pts,
    stroke: highlight,
    strokeWidth: def.diameter_mm * 0.35,
    lineCap: 'round',
    lineJoin: 'round',
    listening: false,
    opacity: 0.85,
    shadowForStrokeEnabled: false,
    hitStrokeWidth: 0,
  });
  group.add(hi);

  return group;
}

/**
 * Renderer para pines planos (sección rectangular tipo lengüeta).
 * Dibuja un trapezoide relleno con degradado transversal metálico.
 * Sin bends para el MVP: se apoya en que el path tiene exactamente
 * 2 vértices (attachment y tip). Si hubiera más, usa la primera y la
 * última coordenada (aproximación adecuada para pines rectos).
 */
function renderFlatPin(
  group: Konva.Group,
  def: PinDef,
  path: { position: { x: number; y: number; z: number } }[],
  metal: string,
  highlight: string,
  flipped: boolean = false,
  rotationDeg: number = 0,
  pinNumber: string = '',
): Konva.Group {
  const attach = path[0]!.position;

  // Suavizamos los codos de la path (mismo enfoque que el pin round):
  // cada bend intermedio se sustituye por un arco discreto de radio
  // proporcional al diámetro del pin, así al doblar los pines quedan
  // redondeados en el punto de doblez en vez de en pico.
  const filletR = Math.max(0.15, def.diameter_mm * 0.6);
  const smoothedRaw = buildSmoothPolyline(path.map((v) => v.position), filletR);
  const positions: { x: number; y: number }[] = [];
  for (let i = 0; i < smoothedRaw.length; i += 2) {
    positions.push({ x: smoothedRaw[i]!, y: smoothedRaw[i + 1]! });
  }
  if (positions.length < 2) return group;

  // Retrasa la base 0.3 mm cuando flipped (el pin entra al cuerpo).
  const insideOffset = flipped ? 0.3 : 0;
  if (insideOffset > 0) {
    const p0 = positions[0]!;
    const p1 = positions[1]!;
    const ddx = p1.x - p0.x;
    const ddy = p1.y - p0.y;
    const dLen0 = Math.hypot(ddx, ddy);
    if (dLen0 > 1e-6) {
      positions[0] = {
        x: p0.x - (ddx / dLen0) * insideOffset,
        y: p0.y - (ddy / dLen0) * insideOffset,
      };
    }
  }

  // Distancia acumulada por vértice.
  const cumDist: number[] = [0];
  for (let i = 1; i < positions.length; i++) {
    const dx = positions[i]!.x - positions[i - 1]!.x;
    const dy = positions[i]!.y - positions[i - 1]!.y;
    cumDist.push(cumDist[i - 1]! + Math.hypot(dx, dy));
  }
  const totalLen = cumDist[cumDist.length - 1]!;
  if (totalLen < 1e-6) return group;

  const wBase = def.diameter_mm / 2;
  const wTip = wBase * (def.tip_ratio ?? 0.55);

  // Reparto hombro / transición:
  //  - Si hay dobleces, se ajustan al PRIMER segmento (transición
  //    completada antes del primer bend).
  //  - Si no (DIP con bend_points_count=0), 45 % / 15 % / 40 %.
  let shoulderLen: number;
  let transitionLen: number;
  if (def.bend_points_count > 0) {
    const segLen = totalLen / (def.bend_points_count + 1);
    shoulderLen = segLen * 0.55;
    transitionLen = segLen * 0.30;
  } else {
    shoulderLen = totalLen * 0.45;
    transitionLen = totalLen * 0.15;
  }

  // Inserta vértices en los puntos donde cambia el ancho (final
  // del hombro y final de la transición) si caen dentro de un
  // segmento existente sin coincidir con un vértice. Sin esto,
  // el pin queda como un trapecio lineal en vez de las 3 partes.
  const insertBreakpoint = (d: number): void => {
    for (let i = 1; i < positions.length; i++) {
      if (d < cumDist[i - 1]! + 1e-9 || d > cumDist[i]! - 1e-9) continue;
      const segLenLocal = cumDist[i]! - cumDist[i - 1]!;
      if (segLenLocal < 1e-6) return;
      const t = (d - cumDist[i - 1]!) / segLenLocal;
      const p = {
        x: positions[i - 1]!.x + (positions[i]!.x - positions[i - 1]!.x) * t,
        y: positions[i - 1]!.y + (positions[i]!.y - positions[i - 1]!.y) * t,
      };
      positions.splice(i, 0, p);
      cumDist.splice(i, 0, d);
      return;
    }
  };
  insertBreakpoint(shoulderLen);
  insertBreakpoint(shoulderLen + transitionLen);

  // Anchura del pin en cada distancia acumulada.
  const widthAt = (d: number): number => {
    if (d <= shoulderLen) return wBase;
    if (d >= shoulderLen + transitionLen) return wTip;
    const t = (d - shoulderLen) / transitionLen;
    return wBase + (wTip - wBase) * t;
  };

  // Perpendicular (bisector) por vértice, con longitud escalada para
  // conservar el ancho del ribbon en cada codo.
  const perps: { x: number; y: number }[] = positions.map((_, i) => {
    let p1x = 0, p1y = 0, p2x = 0, p2y = 0;
    if (i > 0) {
      const dx = positions[i]!.x - positions[i - 1]!.x;
      const dy = positions[i]!.y - positions[i - 1]!.y;
      const l = Math.hypot(dx, dy) || 1;
      p1x = -dy / l;
      p1y = dx / l;
    }
    if (i < positions.length - 1) {
      const dx = positions[i + 1]!.x - positions[i]!.x;
      const dy = positions[i + 1]!.y - positions[i]!.y;
      const l = Math.hypot(dx, dy) || 1;
      p2x = -dy / l;
      p2y = dx / l;
    }
    if (i === 0) return { x: p2x, y: p2y };
    if (i === positions.length - 1) return { x: p1x, y: p1y };
    // Vértice interior: bisector escalado por 1 / cos(θ/2).
    let bx = p1x + p2x;
    let by = p1y + p2y;
    const bLen = Math.hypot(bx, by);
    if (bLen < 1e-6) return { x: p1x, y: p1y };
    bx /= bLen;
    by /= bLen;
    const cosHalf = bx * p1x + by * p1y;
    const scale = Math.abs(cosHalf) > 0.1 ? 1 / cosHalf : 1;
    return { x: bx * scale, y: by * scale };
  });

  const edge = darken(metal, 0.4);
  const shade = darken(metal, 0.15);
  const gradStops = [0, edge, 0.18, shade, 0.5, highlight, 0.82, shade, 1, edge];

  // Rellenamos el ribbon POR SEGMENTOS: cada quad usa su propio
  // gradiente perpendicular al segmento, así el brillo metálico
  // se mantiene "en el centro del pin" también cuando el pin
  // gira/dobla, y no aparece más oscuro tras un bend.
  for (let i = 0; i < positions.length - 1; i++) {
    const p0 = positions[i]!;
    const p1 = positions[i + 1]!;
    const segDx = p1.x - p0.x;
    const segDy = p1.y - p0.y;
    const segLen = Math.hypot(segDx, segDy);
    if (segLen < 1e-6) continue;
    const perpSegX = -segDy / segLen;
    const perpSegY = segDx / segLen;
    const w0 = widthAt(cumDist[i]!);
    const w1 = widthAt(cumDist[i + 1]!);
    const wMax = Math.max(w0, w1, wTip);
    const midX = (p0.x + p1.x) / 2;
    const midY = (p0.y + p1.y) / 2;

    const quad = new Konva.Line({
      points: [
        p0.x + perps[i]!.x * w0, p0.y + perps[i]!.y * w0,
        p1.x + perps[i + 1]!.x * w1, p1.y + perps[i + 1]!.y * w1,
        p1.x - perps[i + 1]!.x * w1, p1.y - perps[i + 1]!.y * w1,
        p0.x - perps[i]!.x * w0, p0.y - perps[i]!.y * w0,
      ],
      closed: true,
      fillLinearGradientStartPoint: {
        x: midX + perpSegX * wMax,
        y: midY + perpSegY * wMax,
      },
      fillLinearGradientEndPoint: {
        x: midX - perpSegX * wMax,
        y: midY - perpSegY * wMax,
      },
      fillLinearGradientColorStops: gradStops,
      listening: true,
      hitStrokeWidth: 0,
    });
    quad.setAttr('nodeKind', 'pin');
    quad.setAttr('pinId', def.id);
    group.add(quad);
  }

  // Contorno del ribbon: una única polilínea cerrada, sólo stroke,
  // encima de los quads. Dibuja el borde oscuro continuo del pin.
  const outlinePoints: number[] = [];
  for (let i = 0; i < positions.length; i++) {
    const w = widthAt(cumDist[i]!);
    outlinePoints.push(
      positions[i]!.x + perps[i]!.x * w,
      positions[i]!.y + perps[i]!.y * w,
    );
  }
  for (let i = positions.length - 1; i >= 0; i--) {
    const w = widthAt(cumDist[i]!);
    outlinePoints.push(
      positions[i]!.x - perps[i]!.x * w,
      positions[i]!.y - perps[i]!.y * w,
    );
  }
  group.add(new Konva.Line({
    points: outlinePoints,
    closed: true,
    stroke: edge,
    strokeWidth: 0.06,
    lineJoin: 'round',
    listening: false,
  }));

  // Punto (posición, tangente, perpendicular) a una distancia dada
  // a lo largo de la path.
  const pointAtDist = (d: number): { p: { x: number; y: number }; perp: { x: number; y: number } } => {
    for (let i = 1; i < positions.length; i++) {
      if (cumDist[i]! >= d) {
        const segLenLocal = cumDist[i]! - cumDist[i - 1]!;
        const t = segLenLocal > 1e-6 ? (d - cumDist[i - 1]!) / segLenLocal : 0;
        const dx = positions[i]!.x - positions[i - 1]!.x;
        const dy = positions[i]!.y - positions[i - 1]!.y;
        const l = Math.hypot(dx, dy) || 1;
        return {
          p: {
            x: positions[i - 1]!.x + dx * t,
            y: positions[i - 1]!.y + dy * t,
          },
          perp: { x: -dy / l, y: dx / l },
        };
      }
    }
    const last = positions[positions.length - 1]!;
    const prev = positions[positions.length - 2]!;
    const dx = last.x - prev.x;
    const dy = last.y - prev.y;
    const l = Math.hypot(dx, dy) || 1;
    return { p: last, perp: { x: -dy / l, y: dx / l } };
  };

  // Línea de división donde acaba la transición trapezoidal y
  // empieza el tip estrecho, perpendicular a la tangente local.
  const seamD = shoulderLen + transitionLen;
  if (seamD < totalLen) {
    const seam = pointAtDist(seamD);
    group.add(new Konva.Line({
      points: [
        seam.p.x + seam.perp.x * wTip, seam.p.y + seam.perp.y * wTip,
        seam.p.x - seam.perp.x * wTip, seam.p.y - seam.perp.y * wTip,
      ],
      stroke: edge,
      strokeWidth: 0.06,
      listening: false,
    }));
  }

  // Compat: mid1 usado por el label del pin number (posición al
  // final del hombro sobre la path).
  const mid1Info = pointAtDist(shoulderLen);
  const mid1 = mid1Info.p;
  const a = positions[0]!;

  // Etiqueta del número de pin (en el centro de la base rectangular),
  // con contra-transformación para que se lea a 0° world y sin invertir.
  if (pinNumber) {
    // Centro visible de la base = punto medio entre borde del cuerpo y mid.
    // Si insideOffset > 0, "a" está dentro del cuerpo; el centro visible
    // es entre attach (borde) y mid.
    const visibleStart = insideOffset > 0 ? attach : a;
    // Sesgamos el centro del texto hacia el final del hombro (mid1)
    // para que quede algo separado del cuerpo, no pegado al borde.
    const t = 0.7;
    const labelCx = visibleStart.x * (1 - t) + mid1.x * t;
    const labelCy = visibleStart.y * (1 - t) + mid1.y * t;

    const ps = flipped ? -1 : 1;
    const labelGroup = new Konva.Group({
      x: labelCx,
      y: labelCy,
      rotation: ps * (0 - rotationDeg),
      scaleY: ps,
      listening: false,
    });
    const fontSize = wBase * 1.68; // texto casi tan alto como la base (20% más grande)
    const boxSize = Math.max(shoulderLen * 0.9, fontSize * 2);
    labelGroup.add(
      new Konva.Text({
        x: -boxSize / 2,
        y: -fontSize / 2,
        width: boxSize,
        height: fontSize,
        text: pinNumber,
        fontSize,
        fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
        fontStyle: 'bold',
        fill: '#111111',
        align: 'center',
        verticalAlign: 'middle',
        listening: false,
      }),
    );
    group.add(labelGroup);
  }

  return group;
}

/**
 * Genera los puntos de una polilínea aplanando cada vértice interior en
 * un arco discreto de radio = filletRadius. Si el segmento adyacente es
 * demasiado corto, el radio efectivo se reduce para no invadir vértices
 * vecinos. Sin doblez visible en un vértice (colineal o cortado), se
 * mantiene el vértice tal cual.
 */
export function buildSmoothPolyline(
  verts: readonly { x: number; y: number; z: number }[],
  filletRadius: number,
): number[] {
  if (verts.length < 2) return verts.flatMap((v) => [v.x, v.y]);
  const out: number[] = [verts[0]!.x, verts[0]!.y];
  const arcSteps = 8;

  for (let i = 1; i < verts.length - 1; i++) {
    const prev = verts[i - 1]!;
    const v = verts[i]!;
    const next = verts[i + 1]!;

    const inX = v.x - prev.x;
    const inY = v.y - prev.y;
    const mIn = Math.hypot(inX, inY);
    const outX = next.x - v.x;
    const outY = next.y - v.y;
    const mOut = Math.hypot(outX, outY);
    if (mIn < 1e-6 || mOut < 1e-6) {
      out.push(v.x, v.y);
      continue;
    }
    const dirInX = inX / mIn;
    const dirInY = inY / mIn;
    const dirOutX = outX / mOut;
    const dirOutY = outY / mOut;
    const dot = Math.max(-1, Math.min(1, dirInX * dirOutX + dirInY * dirOutY));
    if (dot > 0.9997) {
      out.push(v.x, v.y);
      continue;
    }
    const turn = Math.acos(dot);
    if (turn > Math.PI - 0.02) {
      out.push(v.x, v.y);
      continue;
    }

    const wanted = filletRadius * Math.tan(turn / 2);
    const maxSetback = Math.min(mIn, mOut) * 0.49;
    const setback = Math.min(wanted, maxSetback);
    const R = setback / Math.tan(turn / 2);

    const Ax = v.x - dirInX * setback;
    const Ay = v.y - dirInY * setback;
    const Bx = v.x + dirOutX * setback;
    const By = v.y + dirOutY * setback;

    // Normal a dirIn hacia el lado cóncavo (centro del arco).
    const n1x = -dirInY;
    const n1y = dirInX;
    const usePos = n1x * dirOutX + n1y * dirOutY > 0;
    const nx = usePos ? n1x : -n1x;
    const ny = usePos ? n1y : -n1y;
    const Cx = Ax + nx * R;
    const Cy = Ay + ny * R;

    const startAng = Math.atan2(Ay - Cy, Ax - Cx);
    const endAng = Math.atan2(By - Cy, Bx - Cx);
    let sweep = endAng - startAng;
    while (sweep > Math.PI) sweep -= 2 * Math.PI;
    while (sweep <= -Math.PI) sweep += 2 * Math.PI;

    out.push(Ax, Ay);
    for (let k = 1; k < arcSteps; k++) {
      const t = k / arcSteps;
      const a = startAng + sweep * t;
      out.push(Cx + R * Math.cos(a), Cy + R * Math.sin(a));
    }
    out.push(Bx, By);
  }

  const last = verts[verts.length - 1]!;
  out.push(last.x, last.y);
  return out;
}

function darken(hex: string, t: number): string {
  const s = hex.replace('#', '');
  const norm = s.length === 3 ? s.split('').map((c) => c + c).join('') : s;
  const r = parseInt(norm.slice(0, 2), 16);
  const g = parseInt(norm.slice(2, 4), 16);
  const b = parseInt(norm.slice(4, 6), 16);
  return `#${toHex(r * (1 - t))}${toHex(g * (1 - t))}${toHex(b * (1 - t))}`;
}
function toHex(n: number): string {
  const v = Math.round(Math.max(0, Math.min(255, n)));
  return v.toString(16).padStart(2, '0');
}

/**
 * Marcas de los puntos de doblez: línea perpendicular fina al pin.
 * Los que quedan más allá del corte se atenúan y no reciben clics.
 * Atributos { nodeKind: 'bend', pinId, bendIndex }.
 */
export function renderBendMarks(
  def: PinDef,
  state: PinState,
  opts: { tickHalfMm?: number; tickStrokeMm?: number; tickScale?: number } = {},
): Konva.Group {
  const group = new Konva.Group();
  group.setAttr('pinId', def.id);

  const path = computePinPath3D(def, state);
  const cutIdx = state.cut_at_index;

  const scale = opts.tickScale ?? 1;
  const tickHalf = (opts.tickHalfMm ?? def.diameter_mm * 0.9) * scale;
  const tickStroke = Math.max(0.06, (opts.tickStrokeMm ?? def.diameter_mm * 0.14) * scale);

  for (const v of path) {
    if (typeof v.bend_index !== 'number') continue;
    const alive = cutIdx === null ? true : v.bend_index < cutIdx;

    // Perpendicular al pin en el punto: gira 90° la dirección forward.
    const fm = Math.hypot(v.forward.x, v.forward.y);
    let px: number;
    let py: number;
    if (fm > 1e-6) {
      px = -v.forward.y / fm;
      py = v.forward.x / fm;
    } else {
      px = 0;
      py = 1;
    }

    const x1 = v.position.x + px * tickHalf;
    const y1 = v.position.y + py * tickHalf;
    const x2 = v.position.x - px * tickHalf;
    const y2 = v.position.y - py * tickHalf;

    const tick = new Konva.Line({
      points: [x1, y1, x2, y2],
      stroke: alive ? '#1976d2' : '#bdbdbd',
      strokeWidth: tickStroke,
      lineCap: 'round',
      opacity: alive ? 0.55 : 0.3,
      listening: alive,
      hitStrokeWidth: Math.max(tickStroke * 6, def.diameter_mm * 2.5),
    });
    tick.setAttr('nodeKind', 'bend');
    tick.setAttr('pinId', def.id);
    tick.setAttr('bendIndex', v.bend_index);
    group.add(tick);
  }

  return group;
}
